import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Star, Loader2 } from "lucide-react";
import { useAwardFeedback } from "@/hooks/useAwardFeedback";
import { SELECTION_REASONS } from "@/lib/constants/awardFeedback";

interface AwardFeedbackDisplayProps {
  awardId: string;
  isVendorView?: boolean;
}

/**
 * Displays the feedback/reasons why a vendor was selected for an award.
 * Shows selection reasons as badges and any additional comments.
 */
export function AwardFeedbackDisplay({ awardId, isVendorView = false }: AwardFeedbackDisplayProps) {
  const { data: feedback, isLoading } = useAwardFeedback(awardId);

  // Don't render if no feedback or empty reasons
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!feedback || !feedback.selection_reasons || feedback.selection_reasons.length === 0) {
    return null;
  }

  // Get descriptions for each reason - reasons are stored as labels in the database
  const getReasonDescription = (reason: string): string | undefined => {
    const reasonConfig = SELECTION_REASONS.find(r => r.label === reason);
    return reasonConfig?.description;
  };

  return (
    <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/20">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Star className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          {isVendorView ? "Why You Were Selected" : "Selection Reasons"}
        </CardTitle>
        {isVendorView && (
          <p className="text-sm text-muted-foreground">
            The client shared these reasons for choosing your bid
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {feedback.selection_reasons.map((reason: string) => {
            const description = getReasonDescription(reason);
            return (
              <Badge 
                key={reason} 
                variant="secondary"
                className="bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-200"
                title={description}
              >
                {reason}
              </Badge>
            );
          })}
        </div>
        
        {feedback.additional_comments && (
          <div className="pt-2 border-t border-blue-200 dark:border-blue-800">
            <p className="text-sm text-muted-foreground italic">
              "{feedback.additional_comments}"
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
